import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon } from '@/icons';
import { CustomerWithdrawal } from '@/services/withdrawals/customerWithdrawals.types';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ManageCustomerWithdrawalContainer } from '../ManageCustomerWithdrawalContainer';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  onOpen: OnOpenProps,
  activeTab: string,
  t: (key: string) => string,
): ColumnDef<CustomerWithdrawal>[] {
  return [
    {
      accessorKey: 'withdrawalId',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="truncate max-w-[1rem] text-caption font-[500] text-primary">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'withdrawalType',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.customer.table.withdrawType')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-[500] text-primary">{row.getValue('withdrawalType') ?? '-'}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.customer.table.description')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-[500] text-primary">{row.getValue('description') ?? '-'}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'minAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.customer.table.withdrawalRange')}
          className="\whitespace-nowrap"
        />
      ),
      cell: ({ row }) => {
        const minAmount = row.original.minAmount;
        const maxAmount = row.original.maxAmount;
        return (
          <div className="text-caption font-medium text-primary text-right">
            {minAmount}円 - {maxAmount}円
          </div>
        );
      },
      meta: { align: 'right' },
      enableHiding: false,
    },

    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => {
        const id = row.original.withdrawalId;
        return (
          <Can rule={{ allOf: [PERMS.withdrawal.customer.view, PERMS.withdrawal.customer.manage] }}>
            <EditIcon
              className="text-foreground-subtle size-4 hover:cursor-pointer"
              onClick={() =>
                onOpen({
                  title: 'configurationManagement.withdrawalSetup.customer.manageCustomerWithdrawal.title',
                  okText: t('common.page.button.submit'),
                  size: 'lg',
                  modalTitle: 'configurationManagement.withdrawalSetup.customer.manageCustomerWithdrawal.title',
                  component: <ManageCustomerWithdrawalContainer activeTab={activeTab} withdrawalId={id} />,
                })
              }
            />
          </Can>
        );
      },
      size: 40,
      enableHiding: false,
    },
  ];
}
